import React, { useState, useEffect, useCallback } from 'react';
import { customFieldsAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AppDrawer } from './ui/AppDrawer';
import { Plus, Loader2, Settings2, ChevronDown, ChevronRight, X, Layers, Check, Save } from 'lucide-react';
import { toast } from 'sonner';

export function JobCustomFields({ jobId, canEdit }) {
  const [fields, setFields] = useState([]);
  const [assignedTemplateIds, setAssignedTemplateIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // For the picker
  const [allDefs, setAllDefs] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [editingValues, setEditingValues] = useState(false);
  const [valueChanges, setValueChanges] = useState({});

  const fetchJobFields = useCallback(async () => {
    try {
      const res = await customFieldsAPI.getJobFields(jobId);
      setFields(res.data.fields || []);
      setAssignedTemplateIds(res.data.assigned_template_ids || []);
      // Auto-expand if there are fields with values
      if ((res.data.fields || []).some(f => f.value != null && f.value !== '')) {
        setExpanded(true);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [jobId]);

  useEffect(() => { fetchJobFields(); }, [fetchJobFields]);

  const openPicker = async () => {
    try {
      const [dRes, tRes] = await Promise.all([
        customFieldsAPI.listDefinitions(),
        customFieldsAPI.listTemplates(),
      ]);
      setAllDefs(dRes.data.fields || []);
      setAllTemplates(tRes.data.templates || []);
      setPickerOpen(true);
    } catch { toast.error('Failed to load field library'); }
  };

  const handleAssignField = async (fieldId) => {
    try {
      await customFieldsAPI.assignFields(jobId, { field_ids: [fieldId] });
      fetchJobFields();
      toast.success('Field added');
    } catch { toast.error('Failed to add field'); }
  };

  const handleUnassignField = async (fieldId) => {
    try {
      await customFieldsAPI.unassignFields(jobId, { field_ids: [fieldId] });
      fetchJobFields();
      toast.success('Field removed');
    } catch { toast.error('Failed to remove field'); }
  };

  const handleAssignTemplate = async (templateId) => {
    try {
      await customFieldsAPI.assignFields(jobId, { template_ids: [templateId] });
      fetchJobFields();
      toast.success('Field set added');
    } catch { toast.error('Failed to add field set'); }
  };

  const handleUnassignTemplate = async (templateId) => {
    try {
      await customFieldsAPI.unassignFields(jobId, { template_ids: [templateId] });
      fetchJobFields();
      toast.success('Field set removed');
    } catch { toast.error('Failed to remove field set'); }
  };

  const startEditing = () => {
    const initial = {};
    fields.forEach(f => {
      initial[f.id] = f.value != null ? String(f.value) : '';
    });
    setValueChanges(initial);
    setEditingValues(true);
  };

  const saveValues = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(valueChanges).map(([fid, val]) => ({
        field_definition_id: fid,
        value: val || null,
      }));
      await customFieldsAPI.setValues(jobId, payload);
      toast.success('Custom fields saved');
      setEditingValues(false);
      fetchJobFields();
    } catch { toast.error('Failed to save values'); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  if (fields.length === 0 && !canEdit) return null;

  const hasFields = fields.length > 0;

  return (
    <Card data-testid="job-custom-fields">
      <CardHeader className="pb-1 pt-3 px-4">
        <div className="flex items-center justify-between">
          <button className="flex items-center gap-2 text-left" onClick={() => hasFields && setExpanded(!expanded)} data-testid="cf-toggle">
            {hasFields ? (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
            <CardTitle className="text-sm flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Custom Fields
              {hasFields && <Badge variant="secondary" className="text-[10px] h-4">{fields.length}</Badge>}
            </CardTitle>
          </button>
          <div className="flex gap-1">
            {canEdit && editingValues && (
              <Button variant="default" size="sm" className="h-7 text-xs" onClick={saveValues} disabled={saving} data-testid="cf-save-values-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Save
              </Button>
            )}
            {canEdit && hasFields && !editingValues && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={startEditing} data-testid="cf-edit-values-btn">
                Edit
              </Button>
            )}
            {canEdit && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={openPicker} data-testid="cf-manage-btn">
                <Plus className="w-3 h-3 mr-1" /> Manage
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      {expanded && hasFields && (
        <CardContent className="px-4 pb-4 pt-2">
          <div className="space-y-2.5">
            {fields.map(f => (
              <FieldValueRow key={f.id} field={f} editing={editingValues} value={valueChanges[f.id]} onChange={v => setValueChanges(prev => ({ ...prev, [f.id]: v }))} />
            ))}
          </div>
        </CardContent>
      )}

      {!hasFields && canEdit && (
        <CardContent className="px-4 pb-4 pt-1">
          <p className="text-xs text-muted-foreground">No custom fields attached. Click "Manage" to add fields to this job.</p>
        </CardContent>
      )}

      {/* Picker Drawer */}
      <AppDrawer open={pickerOpen} onOpenChange={setPickerOpen} title="Manage Custom Fields" description="Add or remove fields and field sets from this job">
        <div className="space-y-6 p-1">
          {/* Field Sets */}
          {allTemplates.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Layers className="w-4 h-4" /> Field Sets</h4>
              <div className="border rounded-lg divide-y">
                {allTemplates.filter(t => t.is_active).map(t => {
                  const assigned = assignedTemplateIds.includes(t.id);
                  return (
                    <div key={t.id} className="flex items-center gap-3 p-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{t.name}</p>
                        <p className="text-[10px] text-muted-foreground">{t.field_count || 0} fields</p>
                      </div>
                      {assigned ? (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleUnassignTemplate(t.id)} data-testid={`unassign-template-${t.id}`}>
                          <X className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      ) : (
                        <Button variant="default" size="sm" className="h-7 text-xs" onClick={() => handleAssignTemplate(t.id)} data-testid={`assign-template-${t.id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Individual Fields */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Individual Fields</h4>
            {allDefs.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">No custom fields defined. Go to Settings to create them.</p>
            ) : (
              <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                {allDefs.filter(d => d.is_active).map(d => {
                  const assigned = fields.some(f => f.id === d.id);
                  return (
                    <div key={d.id} className="flex items-center gap-3 p-2.5">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">{d.label}</p>
                        <p className="text-[10px] text-muted-foreground">{d.field_type}</p>
                      </div>
                      {assigned ? (
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => handleUnassignField(d.id)} data-testid={`unassign-field-${d.id}`}>
                          <X className="w-3 h-3 mr-1" /> Remove
                        </Button>
                      ) : (
                        <Button variant="default" size="sm" className="h-7 text-xs" onClick={() => handleAssignField(d.id)} data-testid={`assign-field-${d.id}`}>
                          <Plus className="w-3 h-3 mr-1" /> Add
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </AppDrawer>
    </Card>
  );
}


function FieldValueRow({ field, editing, value, onChange }) {
  const displayValue = field.value;
  const isEmpty = displayValue == null || displayValue === '';

  if (!editing) {
    if (isEmpty) return null; // hide empty fields when not editing
    return (
      <div className="flex items-start gap-2" data-testid={`cf-value-${field.id}`}>
        <span className="text-xs text-muted-foreground min-w-[120px] pt-0.5">{field.label}</span>
        <span className="text-sm flex-1">
          {field.field_type === 'checkbox' ? (displayValue === true || displayValue === 'true' ? 'Yes' : 'No')
           : field.field_type === 'currency' ? `$${Number(displayValue).toLocaleString()}`
           : String(displayValue)}
        </span>
      </div>
    );
  }

  // Editing mode
  return (
    <div className="flex items-start gap-2" data-testid={`cf-edit-${field.id}`}>
      <Label className="text-xs min-w-[120px] pt-2">{field.label}{field.is_required && ' *'}</Label>
      <div className="flex-1">
        {field.field_type === 'text' && (
          <Input value={value || ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
        )}
        {field.field_type === 'textarea' && (
          <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2} className="text-sm" />
        )}
        {(field.field_type === 'number' || field.field_type === 'currency') && (
          <Input type="number" step={field.field_type === 'currency' ? '0.01' : '1'} value={value || ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
        )}
        {field.field_type === 'date' && (
          <Input type="date" value={value || ''} onChange={e => onChange(e.target.value)} className="h-8 text-sm" />
        )}
        {field.field_type === 'dropdown' && (
          <Select value={value || '__none'} onValueChange={v => onChange(v === '__none' ? '' : v)}>
            <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">-- None --</SelectItem>
              {(field.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {field.field_type === 'checkbox' && (
          <div className="pt-1">
            <Switch checked={value === 'true'} onCheckedChange={v => onChange(v ? 'true' : 'false')} />
          </div>
        )}
      </div>
    </div>
  );
}
